import * as Alert from '@/components/ui/alert'
import * as Checkbox from '@/components/ui/checkbox'
import * as Field from '@/components/ui/field'
import { Heading } from '@/components/ui/heading'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { FieldInformation } from '@components'
import type { CreateSetup } from '@hvf/contracts'
import { type FormStore, getValue, setValue } from '@modular-forms/solid'
import { For, Match, Show, Switch, createMemo } from 'solid-js'
import { css } from 'styled-system/css'
import { Box, Grid } from 'styled-system/jsx'
import * as v from 'valibot'

import { CheckIcon, Info } from 'lucide-solid'
import type { FormFieldType } from '..'
import { OptionalField } from '../../../../../custom-components/OptionalDetails'
import { ControlledDatePicker } from '../components/ControlledDatePicker'
import { FieldGroupContainer } from '../components/FieldGroupContainer'
import { fieldInformation } from '../config/fieldInformation'
import { TECHNICAL_FIELDS_LONG, TECHNICAL_FIELDS_SHORT } from '../config/technicalFields'
import { TechnicalSchema } from '../schema/createSetupSchema'
import { isSectionValid } from '../utils/formHelpers'
import { Chart } from './Chart'

export default function Technical(props: {
  FormField: FormFieldType
  form: FormStore<CreateSetup, undefined>
  hideNonTechnicalFields?: boolean
}) {
  // 1. Derive the active fields directly based on position
  const formFields = createMemo(() => {
    const position = getValue(props.form, 'position')
    return position === 'short' ? TECHNICAL_FIELDS_SHORT : TECHNICAL_FIELDS_LONG
  })

  // 2. Let Modular Forms handle field-level validity automatically
  const isValid = createMemo(() => isSectionValid(props.form, formFields()))

  // 3. Derive complex cross-field validation errors dynamically
  const HVFErrors = createMemo(() => {
    const formData = formFields().reduce((prev, curr) => {
      prev[curr] = getValue(props.form, curr) as number
      return prev
    }, {} as Record<string, any>)

    const validateResult = v.safeParse(TechnicalSchema, formData)

    return validateResult.issues
      ?.filter(({ message }) => message.includes('HVF_ERROR'))
      .map(({ message }) => message.replace('HVF_ERROR: ', '')) || []
  })

  const getTitle = (key: keyof CreateSetup) => {
    const isShort = getValue(props.form, 'position') === 'short'

    const map: Partial<Record<keyof CreateSetup, string>> = {
      high_1_price: isShort ? 'RH1' : 'H1',
      high_2_price: 'RH2',
      high_3_price: 'RH3',
      low_1_price: isShort ? 'L1' : 'RL1',
      low_2_price: 'RL2',
      low_3_price: 'RL3',
      high_1_time: isShort ? 'RH1 Time' : 'H1 Time',
      high_2_time: 'RH2 Time',
      high_3_time: 'RH3 Time',
      low_1_time: isShort ? 'L1 Time' : 'RL1 Time',
      low_2_time: 'RL2 Time',
      low_3_time: 'RL3 Time',
    }

    return map[key]
  }

  return (
    <FieldGroupContainer
      heading="Technical"
      valid={isValid()}
    >
      <Grid p={1} gap='6'>

        <Chart exchange={getValue(props.form, 'exchange')} symbol={getValue(props.form, 'symbol')} />

        <Grid gridTemplateColumns="1fr 2fr" gap='6'>
          <FieldInformation information={fieldInformation.high_1_price}>
            <Heading textStyle="xs">Price</Heading>
          </FieldInformation>
          <FieldInformation information={fieldInformation.high_1_time}>
            <Heading textStyle="xs">Time</Heading>
          </FieldInformation>

          <For each={formFields()}>
            {(formField) => (
              <props.FormField
                name={formField}
                type={formField.includes('time') ? 'string' : 'number'}
              >
                {(field, formFieldProps) => {
                  return (
                    <Switch>
                      <Match when={formField.includes('time')}>
                        <Field.Root invalid={!!field.error}>
                          <ControlledDatePicker
                            label={getTitle(formField)}
                            value={field.value}
                            setValue={(value) => {
                              setValue(props.form, formField, value || undefined)
                            }}
                          />
                          <Field.ErrorText>
                            {field.error && <div>{field.error}</div>}
                          </Field.ErrorText>
                        </Field.Root>
                      </Match>
                      <Match when={formField.includes('price')}>
                        <Field.Root invalid={!!field.error}>
                          <Field.Label>{getTitle(formField)}</Field.Label>
                          <Input
                            {...formFieldProps}
                            value={field.value ?? '' as any} // TODO fix this type
                            type="number"
                            onChange={(e: Event & { currentTarget: HTMLInputElement }) => {
                              let number: number | undefined = Number(e.currentTarget.value)
                              number = Number.isNaN(number) ? undefined : number
                              setValue(props.form, formField, number)
                            }}
                          />
                          <Field.ErrorText>
                            {field.error && <div>{field.error}</div>}
                          </Field.ErrorText>
                        </Field.Root>
                      </Match>
                    </Switch>
                  )
                }}
              </props.FormField>
            )}
          </For>
        </Grid>

        <Show when={HVFErrors().length}>
          <Box>
            <Alert.Root status='error'>
              <Alert.Indicator asChild={(iconProps) => <Info {...iconProps} />} />
              <Alert.Content>
                <Alert.Title>Technical Issues</Alert.Title>
                <Alert.Description>
                  <Grid>
                    <For each={HVFErrors()}>
                      {(error) => (
                        <Text
                          class={css({ color: 'red.10' })}
                        >
                          {error}
                        </Text>
                      )}
                    </For>
                  </Grid>
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          </Box>
        </Show>

        <Show when={!props.hideNonTechnicalFields}>
          <Box>
            <Heading textStyle="xs" mb='3'>Details</Heading>

            <Grid
              gridTemplateColumns={{
                base: '1fr 1fr',
                mdDown: '1fr',
              }}
              gap='6'
            >
              <props.FormField name="first_in_new_trend" type="boolean">
                {(field, fieldProps) => (
                  <Field.Root invalid={!!field.error}>
                    <Checkbox.Root
                      colorPalette="jade"
                      checked={!!field.value}
                      onCheckedChange={(details) =>
                        setValue(props.form, 'first_in_new_trend', !!details.checked)
                      }
                    >
                      <Checkbox.Control>
                        <Checkbox.Indicator>
                          <CheckIcon />
                        </Checkbox.Indicator>
                      </Checkbox.Control>
                      <Checkbox.Label>
                        <FieldInformation information={fieldInformation.first_in_new_trend}>
                          First in a new trend
                        </FieldInformation>
                      </Checkbox.Label>

                      <Checkbox.HiddenInput {...fieldProps} />
                    </Checkbox.Root>
                  </Field.Root>
                )}
              </props.FormField>

              <props.FormField name="has_primer" type="boolean">
                {(field, fieldProps) => (
                  <Field.Root invalid={!!field.error}>
                    <Checkbox.Root
                      colorPalette="jade"
                      checked={!!field.value}
                      onCheckedChange={(details) =>
                        setValue(props.form, 'has_primer', !!details.checked)
                      }
                    >
                      <Checkbox.Control>
                        <Checkbox.Indicator>
                          <CheckIcon />
                        </Checkbox.Indicator>
                      </Checkbox.Control>
                      <Checkbox.Label>
                        <FieldInformation information={fieldInformation.has_primer}>
                          Has Primer
                        </FieldInformation>
                      </Checkbox.Label>

                      <Checkbox.HiddenInput {...fieldProps} />
                    </Checkbox.Root>
                  </Field.Root>
                )}
              </props.FormField>

              <props.FormField name="trigger_date">
                {(field) => {
                  return (
                    <Field.Root invalid={!!field.error}>
                      <ControlledDatePicker
                        label={
                          <FieldInformation information={fieldInformation.trigger_date}>
                            <OptionalField>Trigger Date</OptionalField> {/* Fixed typo and syntax */}
                          </FieldInformation>
                        }
                        value={field.value}
                        setValue={(value) => {
                          setValue(props.form, 'trigger_date', value || undefined)
                        }}
                      />
                    </Field.Root>
                  )
                }}
              </props.FormField>

              <props.FormField name="time_stop">
                {(field) => (
                  <Field.Root invalid={!!field.error}>
                    <ControlledDatePicker
                      label={<FieldInformation information={fieldInformation.time_stop}>
                        <OptionalField>Time Stop</OptionalField>
                      </FieldInformation>}
                      value={field.value}
                      setValue={(value) => {
                        setValue(props.form, 'time_stop', value || undefined)
                      }}
                    />
                    <Field.ErrorText>
                      {field.error && <div>{field.error}</div>}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              </props.FormField>
            </Grid>
          </Box>
        </Show>
      </Grid>
    </FieldGroupContainer>
  )
}
